import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  createVehicleModel,
  deleteVehicleModel,
  fetchVehicleModelsPage,
  updateVehicleModel,
  type VehicleModelAdmin,
} from "@/lib/catalog/vehicleCatalogAdmin";
import { listBrandRecords, syncBrandsFromApi, type BrandRecord } from "@/lib/catalog/brandsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { slugify } from "@/lib/slugify";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, Car, Edit, Eye, Filter, LayoutGrid, Link2, List, Loader2, Plus, Search, Tag, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "@/components/ui/sonner";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

type VmSortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "brand_asc"
  | "sort_asc"
  | "sort_desc";

function vmSortToApi(sort: VmSortBy): string {
  switch (sort) {
    case "oldest":
      return "created_at:asc";
    case "name_desc":
      return "name:desc";
    case "name_asc":
      return "name:asc";
    case "brand_asc":
      return "brand:asc";
    case "sort_desc":
      return "sort_order:desc";
    case "sort_asc":
      return "sort_order:asc";
    case "latest":
    default:
      return "-created_at";
  }
}

function vehicleModelsDateQuery(
  quickDate: "all" | "today" | "week" | "month",
  filterCreatedDate: Date | undefined,
): { created_on?: string; created_from?: string; created_to?: string } {
  if (filterCreatedDate) {
    return { created_on: format(filterCreatedDate, "yyyy-MM-dd") };
  }
  if (quickDate === "all") return {};
  const now = new Date();
  const end = endOfDay(now).toISOString();
  const start =
    quickDate === "today"
      ? startOfDay(now).toISOString()
      : quickDate === "week"
        ? startOfDay(subDays(now, 6)).toISOString()
        : startOfMonth(now).toISOString();
  return { created_from: start, created_to: end };
}

function formatIsoDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

const VehicleModels = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canViewAny = can(user, "brand_can_view_any");
  const canView = can(user, "brand_can_view");
  const canCreate = can(user, "brand_can_create");
  const canUpdate = can(user, "brand_can_update");
  const canDelete = can(user, "brand_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [items, setItems] = useState<VehicleModelAdmin[]>([]);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [listVersion, setListVersion] = useState(0);
  const [fetching, setFetching] = useState(true);
  /** First API load only — same UX as Employee Allowance Types bootstrap. */
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [brands, setBrands] = useState<BrandRecord[]>(() => listBrandRecords());
  const brandsFetchInFlight = useRef<Promise<BrandRecord[]> | null>(null);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterBrandId, setFilterBrandId] = useState<string>("__all__");
  const [filterName, setFilterName] = useState("");
  const [filterSlug, setFilterSlug] = useState("");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [sortBy, setSortBy] = useState<VmSortBy>("latest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<VehicleModelAdmin | null>(null);
  const [formBrandId, setFormBrandId] = useState("");
  const [formName, setFormName] = useState("");
  const [formPopular, setFormPopular] = useState(false);
  const [formSortOrder, setFormSortOrder] = useState("0");
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VehicleModelAdmin | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewModel, setViewModel] = useState<VehicleModelAdmin | null>(null);

  const filterDepsKey = useMemo(
    () =>
      [
        query,
        sortBy,
        filterBrandId,
        filterName,
        filterSlug,
        quickDate,
        filterCreatedDate ? format(filterCreatedDate, "yyyy-MM-dd") : "",
      ].join("|"),
    [query, sortBy, filterBrandId, filterName, filterSlug, quickDate, filterCreatedDate],
  );
  const prevFilterDepsKey = useRef(filterDepsKey);
  useEffect(() => {
    if (prevFilterDepsKey.current !== filterDepsKey) {
      prevFilterDepsKey.current = filterDepsKey;
      setPage(1);
    }
  }, [filterDepsKey]);

  /** Brands are not required for the grid (API returns `brand_name`). Load from cache or API when user opens filters / add / edit. */
  const ensureBrandsLoaded = useCallback(async (): Promise<BrandRecord[]> => {
    const cached = listBrandRecords();
    if (cached.length > 0) {
      setBrands(cached);
      return cached;
    }
    if (brandsFetchInFlight.current) {
      return brandsFetchInFlight.current;
    }
    const p = (async () => {
      try {
        await syncBrandsFromApi();
        const list = listBrandRecords();
        setBrands(list);
        return list;
      } finally {
        brandsFetchInFlight.current = null;
      }
    })();
    brandsFetchInFlight.current = p;
    return p;
  }, []);

  const loadPage = useCallback(async () => {
    setFetching(true);
    setIsForbidden(false);
    try {
      const dateQ = vehicleModelsDateQuery(quickDate, filterCreatedDate);
      const { data, meta } = await fetchVehicleModelsPage({
        page,
        per_page: pageSize,
        q: query.trim() || undefined,
        brand_id: filterBrandId !== "__all__" ? filterBrandId : undefined,
        name: filterName.trim() || undefined,
        slug: filterSlug.trim() || undefined,
        sort: vmSortToApi(sortBy),
        ...dateQ,
      });
      setItems(data);
      setTotal(meta.total);
      setLastPage(meta.last_page);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[VehicleModels] load failed", error);
      toast.error("Could not load vehicle models.");
      setItems([]);
      setTotal(0);
      setLastPage(1);
    } finally {
      setFetching(false);
      setIsBootstrapping(false);
    }
  }, [
    page,
    pageSize,
    query,
    sortBy,
    filterBrandId,
    filterName,
    filterSlug,
    quickDate,
    filterCreatedDate,
    listVersion,
  ]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const reload = useCallback(() => {
    setListVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  const brandRecordById = useMemo(() => {
    const m = new Map<string, BrandRecord>();
    for (const b of brands) m.set(String(b.id), b);
    return m;
  }, [brands]);

  const brandNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of brands) {
      const id = Number(b.id);
      if (Number.isFinite(id)) m.set(id, b.name);
    }
    return m;
  }, [brands]);

  const appliedFiltersCount =
    (filterBrandId !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterSlug.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterBrandId("__all__");
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterSlug("");
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const totalPages = lastPage;

  const openCreate = () => {
    void (async () => {
      try {
        const list = await ensureBrandsLoaded();
        if (list.length === 0) {
          toast.error("Create a brand first.");
          return;
        }
        setEditing(null);
        setFormBrandId(list[0]?.id ?? "");
        setFormName("");
        setFormPopular(false);
        setFormSortOrder("0");
        setDialogOpen(true);
      } catch {
        toast.error("Could not load brands.");
      }
    })();
  };

  const openEdit = (r: VehicleModelAdmin) => {
    void (async () => {
      try {
        const list = await ensureBrandsLoaded();
        if (list.length === 0) {
          toast.error("Create a brand first.");
          return;
        }
        setEditing(r);
        setFormBrandId(String(r.brand_id));
        setFormName(r.name);
        setFormPopular(r.is_popular);
        setFormSortOrder(String(r.sort_order ?? 0));
        setDialogOpen(true);
      } catch {
        toast.error("Could not load brands.");
      }
    })();
  };

  const save = async () => {
    if (saving) return;
    if (!formBrandId) {
      toast.error("Select a brand.");
      return;
    }
    if (!formName.trim()) {
      toast.error("Name is required.");
      return;
    }
    const slug = slugify(formName).trim();
    if (!slug) {
      toast.error("Name must include letters or numbers.");
      return;
    }
    const sortOrder = Number(formSortOrder);
    if (!Number.isFinite(sortOrder) || sortOrder < 0) {
      toast.error("Sort order must be a non-negative number.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateVehicleModel(editing.id, {
          brand_id: Number(formBrandId),
          name: formName.trim(),
          slug,
          is_popular: formPopular,
          sort_order: sortOrder,
        });
        toast.success("Vehicle model updated.");
      } else {
        await createVehicleModel({
          brand_id: Number(formBrandId),
          name: formName.trim(),
          slug,
          is_popular: formPopular,
          sort_order: sortOrder,
        });
        toast.success("Vehicle model created.");
      }
      setDialogOpen(false);
      await reload();
    } catch (error) {
      console.error("[VehicleModels] save failed", error);
      if (isAxiosError(error) && error.response?.data?.message) {
        toast.error(String(error.response.data.message));
      } else {
        toast.error("Save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteVehicleModel(deleteTarget.id);
      toast.success("Vehicle model deleted.");
      setDeleteTarget(null);
      await reload();
    } catch (error) {
      console.error("[VehicleModels] delete failed", error);
      toast.error("Delete failed.");
    } finally {
      setDeleting(false);
    }
  };

  if (!canViewAny) {
    return (
      <PermissionDenied
        title="Catalog access required"
        message="You need permission to view brands (brand_can_view_any) to manage models."
      />
    );
  }

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Models access required"
        message="You don’t have permission to load vehicle models. Ask an administrator to grant brand catalog access."
      />
    );
  }

  return (
    <Layout title="Models">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Models</h1>
            <p className="text-sm text-muted-foreground mt-1">Vehicle models per brand — used when naming products and in versions</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Add model
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, brand…"
                className="glass-input h-10 pl-9"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet
              open={filtersOpen}
              onOpenChange={(open) => {
                setFiltersOpen(open);
                if (open) {
                  void ensureBrandsLoaded().catch(() => toast.error("Could not load brands."));
                }
              }}
            >
              <SheetTrigger asChild>
                <Button variant="glass" className="shrink-0 gap-2">
                  <Filter className="h-4 w-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter models</SheetTitle>
                  <SheetDescription>Refine by brand, name, slug, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Model</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Brand</Label>
                      <Select value={filterBrandId} onValueChange={setFilterBrandId}>
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All brands" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All brands</SelectItem>
                          {brands.map((b) => (
                            <SelectItem key={b.id} value={String(b.id)}>
                              {b.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Name contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="e.g. Fortuner"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Slug contains</Label>
                      <Input
                        className="glass-input h-10 font-mono text-sm"
                        value={filterSlug}
                        onChange={(e) => setFilterSlug(e.target.value)}
                        placeholder="e.g. fortuner"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal={true}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 shrink-0 overflow-hidden rounded-lg border border-border bg-background">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="vehicle-models-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
              options={[...PAGE_SIZE_OPTIONS]}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as VmSortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  <SelectItem value="brand_asc">Brand, then name</SelectItem>
                  <SelectItem value="sort_asc">Sort order: low to high</SelectItem>
                  <SelectItem value="sort_desc">Sort order: high to low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden items-center justify-between gap-3 md:flex">
            <GridPerPageSelect
              id="vehicle-models-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
              options={[...PAGE_SIZE_OPTIONS]}
            />
            <div className="flex min-w-0 items-center justify-end gap-3">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as VmSortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                    <SelectItem value="brand_asc">Brand, then name</SelectItem>
                    <SelectItem value="sort_asc">Sort order: low to high</SelectItem>
                    <SelectItem value="sort_desc">Sort order: high to low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 font-medium text-foreground"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 shrink-0 overflow-hidden rounded-lg border border-border bg-background">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <>
            {isBootstrapping ? (
              <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading vehicle models…
              </div>
            ) : null}

            {!isBootstrapping && total === 0 && !fetching ? (
              <div className="glass-card p-6 text-sm text-muted-foreground">
                {query.trim() ||
                filterBrandId !== "__all__" ||
                filterName.trim() ||
                filterSlug.trim() ||
                filterCreatedDate ||
                quickDate !== "all"
                  ? "No models match your filters."
                  : canCreate
                    ? "No models yet. Click “Add model” to create one."
                    : "No models yet."}
              </div>
            ) : null}

            {!isBootstrapping && total > 0 && viewMode === "grid" ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {items.map((r, i) => {
                  const br = brandRecordById.get(String(r.brand_id));
                  const thumb = br?.logoDataUrl;
                  const brandLabel = r.brand_name || brandNameById.get(r.brand_id) || "—";
                  return (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <div className="rounded-lg border border-border bg-card text-card-foreground overflow-hidden shadow-sm">
                        <div className="h-1 w-full bg-primary" aria-hidden />
                        <div className="p-4 pt-5 space-y-4">
                          <div className="flex gap-3 items-start">
                            {thumb ? (
                              <img
                                src={thumb}
                                alt=""
                                className="h-12 w-12 rounded-lg object-contain border border-border shrink-0 bg-muted/30 p-0.5"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0 border border-border/50">
                                <Car className="w-6 h-6 text-muted-foreground/60" />
                              </div>
                            )}
                            <h3 className="text-lg font-bold uppercase tracking-tight text-foreground leading-snug break-words flex-1 min-w-0">
                              {r.name}
                            </h3>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-3">
                            <div className="flex gap-2.5 items-start min-w-0">
                              <Link2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" strokeWidth={2} />
                              <div className="min-w-0">
                                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Slug</div>
                                <div className="font-semibold text-sm text-foreground truncate font-mono" title={r.slug}>
                                  {r.slug}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2.5 items-start min-w-0">
                              <Tag className="w-4 h-4 shrink-0 mt-0.5 text-red-600" strokeWidth={2} />
                              <div className="min-w-0">
                                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Brand</div>
                                <div className="font-semibold text-sm text-foreground truncate" title={brandLabel}>
                                  {brandLabel}
                                </div>
                              </div>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {r.is_popular ? "Popular · " : ""}Sort order {r.sort_order}
                          </p>
                          <div className="border-t border-border pt-3">
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              Updated: {formatCatalogAuditDate(r.updated_at ?? r.created_at)}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {canView ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 min-w-[5.5rem] gap-2 h-9 text-xs font-medium"
                                onClick={() => setViewModel(r)}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View
                              </Button>
                            ) : null}
                            {canUpdate ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 min-w-[5.5rem] gap-2 h-9 text-xs font-medium"
                                onClick={() => openEdit(r)}
                              >
                                <Edit className="w-3.5 h-3.5" />
                                Edit
                              </Button>
                            ) : null}
                            {canDelete ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-9 w-9 shrink-0 p-0 border-destructive/60 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setDeleteTarget(r)}
                                aria-label="Delete model"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : null}

            {!isBootstrapping && total > 0 && viewMode === "list" ? (
              <div className="space-y-3">
                {items.map((r, i) => {
                  const br = brandRecordById.get(String(r.brand_id));
                  const thumb = br?.logoDataUrl;
                  const brandLabel = r.brand_name || brandNameById.get(r.brand_id) || String(r.brand_id);
                  return (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className="glass-card p-3"
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                          {thumb ? (
                            <img src={thumb} alt="" className="h-full w-full object-contain p-1" />
                          ) : (
                            <Car className="w-8 h-8 text-muted-foreground/40" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm font-semibold text-foreground truncate">{r.name}</h3>
                            <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-600 font-mono truncate max-w-[140px]">
                              {r.slug}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {brandLabel}
                            {r.is_popular ? " · Popular" : ""} · Sort {r.sort_order} · Updated{" "}
                            {formatCatalogAuditDate(r.updated_at ?? r.created_at)}
                          </p>
                        </div>
                        <div className="ml-auto flex items-center gap-2">
                          {canView ? (
                            <Button variant="glass" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setViewModel(r)}>
                              <Eye className="h-3 w-3" /> View
                            </Button>
                          ) : null}
                          {canUpdate ? (
                            <Button variant="glass" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => openEdit(r)}>
                              <Edit className="h-3 w-3" /> Edit
                            </Button>
                          ) : null}
                          {canDelete ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteTarget(r)}
                              aria-label="Delete model"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : null}

            {!isBootstrapping && total > 0 ? (
              <GridPaginationFooter
                page={page}
                totalPages={totalPages}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                totalCount={total}
                onPageChange={setPage}
              />
            ) : null}
        </>

        <Dialog open={!!viewModel} onOpenChange={(o) => !o && setViewModel(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Model details</DialogTitle>
            </DialogHeader>
            {viewModel ? (() => {
              const vbLogo = brandRecordById.get(String(viewModel.brand_id))?.logoDataUrl;
              return (
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
                    {vbLogo ? (
                      <img src={vbLogo} alt="" className="h-full w-full object-contain p-1" />
                    ) : (
                      <Car className="h-8 w-8 text-muted-foreground/60" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-foreground">{viewModel.name}</p>
                    <p className="mt-0.5 font-mono text-xs text-muted-foreground">{viewModel.slug}</p>
                  </div>
                </div>
                <dl className="grid grid-cols-1 gap-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Brand</dt>
                    <dd className="text-muted-foreground">{viewModel.brand_name || brandNameById.get(viewModel.brand_id)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Popular</dt>
                    <dd className="text-muted-foreground">{viewModel.is_popular ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Sort order</dt>
                    <dd className="text-muted-foreground">{viewModel.sort_order}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Created</dt>
                    <dd className="text-muted-foreground">{formatIsoDate(viewModel.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Last updated</dt>
                    <dd className="text-muted-foreground">{formatIsoDate(viewModel.updated_at)}</dd>
                  </div>
                </dl>
              </div>
              );
            })() : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewModel(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit vehicle model" : "Add vehicle model"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Brand *</Label>
                <Select value={formBrandId} onValueChange={setFormBrandId}>
                  <SelectTrigger className="glass-input h-10">
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Name *</Label>
                <Input
                  className="glass-input h-10"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Fortuner"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="vm-pop" checked={formPopular} onCheckedChange={(c) => setFormPopular(Boolean(c))} />
                <Label htmlFor="vm-pop" className="text-sm text-muted-foreground cursor-pointer">
                  Popular
                </Label>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Sort order</Label>
                <Input
                  className="glass-input h-10"
                  type="number"
                  min={0}
                  value={formSortOrder}
                  onChange={(e) => setFormSortOrder(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="button" variant="premium" disabled={saving} onClick={() => void save()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={deleteTarget != null}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this vehicle model?"
          description={
            deleteTarget ? (
              <>
                <span>
                  Remove <strong className="text-foreground">{deleteTarget.name}</strong> and its catalog link? Versions
                  under this model should be removed first, or the delete may fail if the database prevents it.
                </span>
              </>
            ) : (
              ""
            )
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void remove()}
        />
      </div>
    </Layout>
  );
};

export default VehicleModels;
